import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle, MapPin, Tent, Clock, User, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, money } from "@/lib/format";

const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function VenueApprovalPanel() {
  const qc = useQueryClient();
  const pending = useQuery({ queryKey: ["admin-venue-pending"], queryFn: () => api.get("/admin/venue/pending").then((r) => r.data) });

  const decide = async (kind, id, action) => {
    try {
      const url = kind === "hold" ? `/admin/venue/holds/${id}` : `/admin/venue/retreats/${id}`;
      await api.put(url, { action });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      qc.invalidateQueries({ queryKey: ["admin-venue-pending"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const holds = pending.data?.holds || [];
  const retreats = pending.data?.retreats || [];
  const empty = !pending.isLoading && holds.length === 0 && retreats.length === 0;

  return (
    <div data-testid="admin-venue-panel">
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-[#1A242B]">Venue approvals</h2>
        <p className="text-sm text-[#4A5D6B]">Review Enhancer requests for retreat spaces. Wellness rooms auto-confirm and never appear here.</p>
      </div>

      {pending.isLoading && <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading requests…</p>}
      {empty && <div className="eyw-card p-8 text-center text-[#4A5D6B]" data-testid="admin-venue-empty">No pending venue requests. You're all caught up.</div>}

      {retreats.length > 0 && (
        <div className="mb-8">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[#B5794E] uppercase tracking-wide mb-3"><Tent size={16} /> Retreat proposals</h3>
          <div className="space-y-3">
            {retreats.map((e) => (
              <div key={e.id} className="eyw-card p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`admin-venue-retreat-${e.id}`}>
                <div>
                  <div className="text-base font-semibold text-[#1A242B]">{e.title}</div>
                  <div className="text-xs text-[#4A5D6B] mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {e.location}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {fmtDate(e.date)} · {fmt12(e.start_time)}–{fmt12(e.end_time)}</span>
                    <span className="flex items-center gap-1"><User size={12} /> {e.enhancer_name}</span>
                    <span>Capacity {e.capacity} · {money(Number(e.price || 0))}</span>
                  </div>
                  {e.description && <p className="text-xs text-[#4A5D6B] mt-2 max-w-xl">{e.description}</p>}
                </div>
                <Actions onApprove={() => decide("retreat", e.id, "approve")} onReject={() => decide("retreat", e.id, "reject")} idPrefix={`retreat-${e.id}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {holds.length > 0 && (
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[#144F75] uppercase tracking-wide mb-3"><MapPin size={16} /> Room reservation requests</h3>
          <div className="space-y-3">
            {holds.map((h) => (
              <div key={h.id} className="eyw-card p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`admin-venue-hold-${h.id}`}>
                <div>
                  <div className="text-base font-semibold text-[#1A242B]">{h.title || h.room_name}</div>
                  <div className="text-xs text-[#4A5D6B] mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {h.room_name}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {fmtDate(h.date)} · {fmt12(h.start_time)}–{fmt12(h.end_time)}</span>
                    <span className="flex items-center gap-1"><User size={12} /> {h.enhancer_name}</span>
                  </div>
                  {h.purpose && <p className="text-xs text-[#4A5D6B] mt-2 max-w-xl">{h.purpose}</p>}
                </div>
                <Actions onApprove={() => decide("hold", h.id, "approve")} onReject={() => decide("hold", h.id, "reject")} idPrefix={`hold-${h.id}`} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Actions = ({ onApprove, onReject, idPrefix }) => (
  <div className="flex gap-2">
    <button onClick={onApprove} data-testid={`admin-venue-approve-${idPrefix}`} className="eyw-btn !py-2 !px-4 text-sm"><CheckCircle2 size={15} /> Approve</button>
    <button onClick={onReject} data-testid={`admin-venue-reject-${idPrefix}`} className="eyw-btn-ghost !py-2 !px-4 text-sm !border-red-300 !text-red-600 hover:!bg-red-500 hover:!text-white"><XCircle size={15} /> Reject</button>
  </div>
);
